import { differenceInCalendarMonths, addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addWeeks, format, eachDayOfInterval, max, min } from 'date-fns';
import { read } from 'shapefile';
import jszip from 'jszip';
import * as topojson from 'topojson-client';
import { kml } from '@tmcw/togeojson';
import TerraformerWKTParser from 'terraformer-wkt-parser';

/* DATE FUNCTIONS */

// this function is used to get the formatted date
// for ex: dateString = '2024-05-10T16:21:52.933544+05:30' returns '10/05/2024'
export const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(); // Use local timezone and format
}

// for ex: dateString = '2024-06-29T18:16:11.537Z' returns '29 June 2024, 10:24:36 pm'
export const formatFullDate = (dateString) => {
    const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    const timeOptions = { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true };
    const date = new Date(dateString).toLocaleDateString(undefined, dateOptions);
    const time = new Date(dateString).toLocaleTimeString(undefined, timeOptions);
    return `${date}, ${time}`
};

// for ex: dateString = '2026-09-12T18:16:11.537Z' returns '12 Sep 2026'
// en-GB forces day-month-year order regardless of the browser's own locale
export const formatDateOnly = (dateString) => {
    const dateOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-GB', dateOptions);
};

/* Returns array of the Months in between the given range */
/* for ex: start = 2024-06-29T18:16:11.537Z
    end = 2024-07-18T18:16:11.537Z
    return value = ['JUNE 2024', 'JULY 2024'];
*/

/**
   * Returns all weeks that intersect with a month
   */
export const getWeeksForMonth = (monthStart, monthEnd) => {
    const weeks = [];
  
    let currentWeekStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  
    while (currentWeekStart <= monthEnd) {
      const isoWeekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
  
      const effectiveWeekStart = max([currentWeekStart, monthStart]);
      const effectiveWeekEnd = min([isoWeekEnd, monthEnd]);
  
      const days = eachDayOfInterval({
        start: effectiveWeekStart,
        end: effectiveWeekEnd,
      }).map(day => ({
        date: day.toISOString(),
        dayNumber: day.getDate(),
      }));
  
      weeks.push({
        weekStart: effectiveWeekStart.toISOString(),
        weekEnd: effectiveWeekEnd.toISOString(),
        days,
      });
  
      currentWeekStart = addWeeks(currentWeekStart, 1);
    }
  
    return weeks;
  };

export const getMonthsInRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const monthsDifference = differenceInCalendarMonths(endDate, startDate);
    const monthsArray = [];

    for (let i = 0; i <= monthsDifference; i++) {
        const currentMonth = addMonths(startDate, i);

        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);

        const rawWeeks = getWeeksForMonth(monthStart, monthEnd);

        const weeks = rawWeeks.map((week, index) => {
            const weekIndex = index + 1;

            return {
                weekId: `${format(currentMonth, 'yyyy-MM')}-W${weekIndex}`,
                weekLabel: `W ${String(weekIndex).padStart(2, '0')}`,
                weekIndex,
                weekStart: week.weekStart,
                weekEnd: week.weekEnd,
                days: week.days,
            };
        });

        monthsArray.push({
            monthName: format(currentMonth, 'MMMM yyyy').toUpperCase(),
            monthStartEndDates: {
                startDate: monthStart.toISOString(),
                endDate: monthEnd.toISOString(),
            },
            weeks,
        });
    }

    return monthsArray;
};

/* CONVERSION FUNCTIONS */

// list: constant having keys label and value
// search: 'term needs to be searched'

// this function returns the server value for the label displayed on UI
export const getValue = (list, search) => {
    const item = list.find(item => item.label === search);
    return item ? item.value : null;
}

// this function returns the label for the label server value
export const getLabel = (list, search) => {
    const item = list.find(item => item.value === search);
    return item ? item.label : null;
}

/* GEOJSON & MAPBOX */

// calculate bbox with the coords

export const calculateBoundingBoxFromCoords = (coordinates) => {
    // Initialize with 0
    let minLon = Infinity;
    let minLat = Infinity;
    let maxLon = -Infinity;
    let maxLat = -Infinity;

    // // Adjust longitudes to ensure they fall within the -180 to 180 range
    // coordinates = coordinates.map(([lon, lat]) => {
    //     if (lon < -180) lon += 360;
    //     if (lon > 180) lon -= 360;
    //     return [lon, lat];
    // });

    // Find minimum and maximum longitude and latitude
    coordinates.forEach(([lon, lat]) => {
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
    });

    // Round to two decimal places
    minLon = parseFloat(minLon.toFixed(2));
    minLat = parseFloat(minLat.toFixed(2));
    maxLon = parseFloat(maxLon.toFixed(2));
    maxLat = parseFloat(maxLat.toFixed(2));

    // Return the formatted string of four coordinates
    // return `${minLon},${minLat},${maxLon},${maxLat}`;

    // Return the flat array of four coordinates
    return [minLon, minLat, maxLon, maxLat];
}

// this function take the geojson of a polygon and return bbox array
// for ex: return value will be like [-188.89, 63.29, -162.28, 72.02]

export const calculateBoundingBox = (geojson) => {

    if (!geojson || !geojson.features || geojson.features.length === 0) {
        return [0, 0, 0, 0]; // Default bounding box centered at the origin
    }

    const coordinates = geojson.features[0].geometry.coordinates[0];

    return calculateBoundingBoxFromCoords(coordinates);

};

// Function to convert MultiPolygon to Polygon
export const convertMultiPolygonToPolygon = (multiPolygon) => {
    let polygons = [];
    for (const coordinate of multiPolygon.coordinates) {
        let featureShape = {
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'Polygon',
                coordinates: coordinate,
            },
        };
        polygons.push(featureShape);
    }
    return polygons;
};

// Function to handle file upload to convert differnt shpae files into geojson format
// Supports format like geojson, json, kml, wkt, topojson, zip, shapefile

export const handleFileUpload = async (e, callback) => {
    let geojson = {};
    const file = e.target.files[0];
    const formatFileName = file && file.name.toLowerCase();

    if (formatFileName) {
        if (formatFileName.endsWith('.zip')) {
            const arrayBuffer = await file.arrayBuffer();
            const uploadedFiles = await jszip.loadAsync(arrayBuffer);
            const files = Object.values((uploadedFiles && uploadedFiles.files));
            const features = [];
            if (files.length > 0) {
                for (const file of files) {
                    if (file.name.endsWith('.shp')) {
                        const shapefileBuffer = await file.async('arraybuffer');
                        const shapeData = await read(shapefileBuffer);
                        const geometry = shapeData.features[0].geometry;
                        if (geometry.type === 'MultiPolygon' && geometry.coordinates.length > 1) {
                            features.push(...convertMultiPolygonToPolygon(geometry));
                        } else {
                            features.push((shapeData && shapeData.features[0]));
                        }
                    }
                }
            }
            geojson = {
                type: 'FeatureCollection',
                features,
            };
        } else if (formatFileName.endsWith('.geojson') || formatFileName.endsWith('.json')) {
            const text = await file.text();
            geojson = JSON.parse(text);
        } else if (formatFileName.endsWith('.topojson')) {
            const text = await file.text();
            const topojsonData = JSON.parse(text);
            const firstObjectKey = Object.keys(topojsonData.objects)[0];
            const firstObject = topojsonData.objects[firstObjectKey];
            geojson = topojson.feature(topojsonData, firstObject);
        } else if (formatFileName.endsWith('.kml')) {
            const text = await file.text();
            const parser = new DOMParser();
            const kmlData = parser.parseFromString(text, 'application/xml');
            geojson = kml(kmlData);
        } else if (formatFileName.endsWith('.wkt')) {
            const text = await file.text();
            const parsedJson = TerraformerWKTParser.parse(text);
            geojson = {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        geometry: parsedJson,
                        properties: {},
                    },
                ],
            };
        } else {
            console.error('Unsupported file format');
        }

        const fileInfo = {
            formatFileName,
            geojson,
        };

        if (callback && typeof callback === 'function') {
            callback(fileInfo);
        }
    }
};


// Function to calculate bounding box coordinates for a polygon when you only have the coordinates and type
export const calculateBoundingBoxCoordinates = (input) => {
    if (!input) return null;

    let coords = [];

    const extract = (geom) => {
        if (!geom) return; // skip null/undefined

        // Feature → drill into geometry
        if (geom.type === "Feature") {
            return extract(geom.geometry);
        }

        // FeatureCollection → process all
        if (geom.type === "FeatureCollection") {
            geom.features.forEach(f => extract(f.geometry));
            return;
        }

        // Geometry types
        switch (geom.type) {
            case "Point":
                if (Array.isArray(geom.coordinates))
                    coords.push(geom.coordinates);
                break;

            case "MultiPoint":
            case "LineString":
                if (Array.isArray(geom.coordinates))
                    coords = coords.concat(geom.coordinates);
                break;

            case "MultiLineString":
            case "Polygon":
                if (Array.isArray(geom.coordinates))
                    geom.coordinates.forEach(ring => {
                        coords = coords.concat(ring);
                    });
                break;

            case "MultiPolygon":
                if (Array.isArray(geom.coordinates))
                    geom.coordinates.forEach(poly => {
                        poly.forEach(ring => {
                            coords = coords.concat(ring);
                        });
                    });
                break;

            default:
                // ignore unsupported or undefined geometry silently
                return;
        }
    };

    extract(input);

    if (coords.length === 0) return null;

    let minLng = Infinity, minLat = Infinity;
    let maxLng = -Infinity, maxLat = -Infinity;

    coords.forEach(([lng, lat]) => {
        minLng = Math.min(minLng, lng);
        minLat = Math.min(minLat, lat);
        maxLng = Math.max(maxLng, lng);
        maxLat = Math.max(maxLat, lat);
    });

    return [[minLng, minLat], [maxLng, maxLat]];
};

// Output: [[15.967548902876587, 7.511277286887932], [30.33146001029243, 8.189590707797237]]
// src/common/utils/mapUtils.js

/**
 * Smoothly fly the map view to a location with pixel offset.
 *
 * @param {mapboxgl.Map} map - Mapbox GL JS map instance.
 * @param {LngLatLike | mapboxgl.MapMouseEvent} target - Target position or click event.
 * @param {Object} [offset={}] - Pixel offset from the target position.
 * @param {number} [offset.x=0] - Horizontal offset (positive = right, negative = left).
 * @param {number} [offset.y=0] - Vertical offset (positive = down, negative = up).
 */
export const flyToWithOffset = (map, target, offset = { x: 0, y: 0 }, options = {}) => {
    if (!map || !target) return;
  
    // Derive target coordinates
    const lngLat = target?.lngLat
      ? [target?.lngLat?.lng, target?.lngLat?.lat] // click event
      : Array.isArray(target)
      ? target
      : [target?.lng, target?.lat];
  
    // Convert to pixel position
    const pixel = map.project(lngLat);
  
    // Apply offsets (default: no movement)
    pixel.x += offset.x || 0;
    pixel.y += offset.y || 0;
  
    // Convert back to geographic coordinates
    const offsetCenter = map.unproject(pixel);
  
    // Perform the fly animation
    map.flyTo({
      center: offsetCenter,
      essential: true,
      ...options,
    });
  };
  

  // Function to get centroid point from bbox
  export const getCentroidFromBbox = (bbox) => {
    // bbox: [minLng, minLat, maxLng, maxLat]
    const [minLng, minLat, maxLng, maxLat] = bbox;
  
    return {
      type: "Point",
      coordinates: [
        (minLng + maxLng) / 2,
        (minLat + maxLat) / 2,
      ],
    };
  };

  export const getPopupLngLat = (e) => {
    const feature = e.features[0];
  
    if (feature.geometry.type === 'Point') {
      return feature.geometry.coordinates;
    }
  
    return e.lngLat;
  };